import React, { useState, useRef, useEffect } from 'react';
import { Button, Form, Input, Card, message, Divider, Typography } from 'antd';
import {
  LockOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
  MailOutlined
} from '@ant-design/icons';

const { Title, Text, Link } = Typography;

// Add CSS animations - uses transform (GPU-composited) instead of background-position (causes repaint)
const animations = `
  @keyframes gradientShift {
    0% { transform: translate(0%, 0%); }
    25% { transform: translate(-25%, -10%); }
    50% { transform: translate(-50%, 0%); }
    75% { transform: translate(-25%, 10%); }
    100% { transform: translate(0%, 0%); }
  }

  .login-bg-animated {
    position: absolute;
    top: -50%;
    left: -50%;
    width: 300%;
    height: 300%;
    background: linear-gradient(135deg, var(--login-primary) 0%, var(--login-secondary) 20%, #f093fb 40%, #f5576c 60%, #4facfe 80%, var(--login-primary) 100%);
    animation: gradientShift 15s ease infinite;
    will-change: transform;
    z-index: 0;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.innerText = animations;
  document.head.appendChild(styleSheet);
}

interface LoginFormData {
  email: string;
  password: string;
  remember?: boolean;
}

export interface LoginUserData {
  id: string;
  email: string;
  name: string;
  avatar?: string;
  accessToken: string;
  refreshToken: string;
  provider?: string;
  role?: string;
}

export interface LoginProps {
  onLogin?: (userData: LoginUserData) => void;
  onGoogleLogin?: (credential?: string) => Promise<void>;
  onForgotPassword?: (email: string) => Promise<void>;
  onRegister?: () => void;
  title?: string;
  subtitle?: string;
  logo?: React.ReactNode;
  apiLogin: (data: LoginFormData) => Promise<LoginUserData>;
  showGoogleLogin?: boolean;
  showRegisterLink?: boolean;
  showForgotPassword?: boolean;
  showRememberMe?: boolean;
  primaryColor?: string;
  secondaryColor?: string;
}

const Login: React.FC<LoginProps> = ({ 
  onLogin,
  onGoogleLogin,
  onForgotPassword,
  onRegister,
  title = 'Welcome Back to Cleveroo',
  subtitle = 'Sign in to your account to continue',
  logo,
  apiLogin,
  showGoogleLogin = true,
  showRegisterLink = true,
  showForgotPassword = true,
  showRememberMe = true,
  primaryColor = '#667eea',
  secondaryColor = '#764ba2'
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const handleLogin = async (values: LoginFormData) => {
    setLoading(true);
    try {
      console.log('🔐 Starting login with:', { email: values.email });
      
      const userData = await apiLogin(values);
      
      // Store tokens in localStorage
      localStorage.setItem('accessToken', userData.accessToken);
      localStorage.setItem('refreshToken', userData.refreshToken);
      console.log('💾 Tokens stored in localStorage');
      
      message.success('Successfully signed in!');
      
      console.log('👤 Calling onLogin with userData:', userData);
      console.log('🔗 onLogin function exists:', !!onLogin);
      
      if (onLogin) {
        onLogin(userData);
        console.log('✅ onLogin callback executed');
      } else {
        console.warn('⚠️ onLogin callback is not provided');
      }
    } catch (error: any) {
      console.error('❌ Login error:', error);
      message.error(error.message || 'Sign in failed. Please check your email and password.');
    } finally {
      setLoading(false);
    }
  };

  const googleButtonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showGoogleLogin || !onGoogleLogin) return;
    const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!googleClientId) return;

    const initGoogle = () => {
      if (!window.google || !googleButtonRef.current) return false;

      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: async (response: any) => {
          setGoogleLoading(true);
          try {
            await onGoogleLogin(response.credential);
          } catch (error: any) {
            console.error('Google OAuth error:', error);
            message.error(error.message || 'Google sign in failed');
          } finally {
            setGoogleLoading(false);
          }
        },
      });

      (window.google.accounts.id as any).renderButton(googleButtonRef.current, {
        theme: 'outline',
        size: 'large',
        width: googleButtonRef.current.offsetWidth,
        text: 'continue_with',
        shape: 'pill',
      });
      return true;
    };

    if (!initGoogle()) {
      const interval = setInterval(() => {
        if (initGoogle()) clearInterval(interval);
      }, 200);
      return () => clearInterval(interval);
    }
  }, [showGoogleLogin, onGoogleLogin]);

  const handleForgotPasswordClick = async () => {
    try {
      // Get email from form
      const email = form.getFieldValue('email');
      
      if (!email) {
        message.warning('Please enter your email address first');
        return;
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        message.error('Please enter a valid email address');
        return;
      }

      if (onForgotPassword) {
        await onForgotPassword(email);
        message.success('Password reset email sent! Please check your inbox.');
      } else {
        message.info('Password reset feature is not available.');
      }
    } catch (error: any) {
      message.error(error.message || 'Failed to send reset email. Please try again.');
    }
  };

  const handleRegisterClick = () => {
    if (onRegister) {
      onRegister();
    } else {
      message.info('Registration feature coming soon!');
    }
  };

  return (
    <div
      className="login-background"
      style={{
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        // @ts-ignore
        '--login-primary': primaryColor,
        '--login-secondary': secondaryColor,
      } as React.CSSProperties}
    >
      {/* Animated gradient background - uses transform (GPU) instead of background-position (CPU) */}
      <div className="login-bg-animated" />
      
      <div style={{ width: '100%', maxWidth: '28rem', position: 'relative', zIndex: 10 }}>
        {/* Logo and Title */}
        <div className="text-center mb-8">
          {logo && (
            <div 
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                paddingTop: '2rem',
                marginBottom: '2rem'
              }}
            >
              <div 
                className="login-float"
                style={{
                  position: 'relative',
                  animation: 'float 6s ease-in-out infinite'
                }}
              >
                <div 
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                    borderRadius: '50%',
                    filter: 'blur(15px)',
                    opacity: 0.4,
                    animation: 'glow 2s ease-in-out infinite alternate'
                  }}
                ></div>
                {logo}
              </div>
            </div>
          )}
          <Title
            level={2}
            style={{
              fontSize: '2.5rem',
              fontWeight: '800',
              color: 'white',
              textAlign: 'center',
              marginBottom: '1rem',
              fontFamily: '"Inter", sans-serif',
              textShadow: '0 2px 10px rgba(0,0,0,0.2)'
            }}
          >
            {title}
          </Title>
          <Text
            style={{
              color: 'rgba(255, 255, 255, 0.85)',
              fontSize: '1.125rem',
              textAlign: 'center',
              display: 'block',
              marginBottom: '0.5rem'
            }}
          >
            {subtitle}
          </Text>
        </div>

        {/* Login Card */}
        <Card 
          className="login-card shadow-2xl border-0 rounded-3xl"
          style={{
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.3)',
            borderRadius: '24px',
            backdropFilter: 'blur(20px)',
            background: 'rgba(255, 255, 255, 0.95)',
            border: '1px solid rgba(255, 255, 255, 0.3)'
          }}
        >
          <Form
            form={form}
            name="login"
            onFinish={handleLogin}
            layout="vertical"
            size="large"
            requiredMark={false}
          >
            <Form.Item
              name="email"
              label={
                <span 
                  style={{
                    color: '#374151',
                    fontWeight: '600',
                    fontSize: '14px',
                    marginBottom: '8px'
                  }}
                >
                  Email Address
                </span>
              }
              rules={[
                { required: true, message: 'Please enter your email address' },
                { type: 'email', message: 'Please enter a valid email address' }
              ]}
              style={{ marginBottom: '20px' }}
            >
              <Input
                prefix={<MailOutlined style={{ color: '#9ca3af', fontSize: '16px' }} />}
                placeholder="Enter your email address"
                style={{
                  height: '52px',
                  borderRadius: '16px',
                  border: '2px solid #e5e7eb',
                  fontSize: '16px',
                  fontWeight: '400',
                  padding: '0 16px',
                  backgroundColor: '#ffffff',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                  transition: 'all 0.2s ease-in-out'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = primaryColor;
                  e.target.style.boxShadow = `0 0 0 3px ${primaryColor}1a`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)';
                }}
              />
            </Form.Item>

            <Form.Item
              name="password"
              label={
                <span 
                  style={{
                    color: '#374151',
                    fontWeight: '600',
                    fontSize: '14px',
                    marginBottom: '8px'
                  }}
                >
                  Password
                </span>
              }
              rules={[
                { required: true, message: 'Please enter your password' },
                { min: 6, message: 'Password must be at least 6 characters' }
              ]}
              style={{ marginBottom: '20px' }}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: '#9ca3af', fontSize: '16px' }} />}
                placeholder="Enter your password"
                style={{
                  height: '52px',
                  borderRadius: '16px',
                  border: '2px solid #e5e7eb',
                  fontSize: '16px',
                  fontWeight: '400',
                  padding: '0 16px',
                  backgroundColor: '#ffffff',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                  transition: 'all 0.2s ease-in-out'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = primaryColor;
                  e.target.style.boxShadow = `0 0 0 3px ${primaryColor}1a`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)';
                }}
                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
              />
            </Form.Item>

            <div 
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '2rem'
              }}
            >
              {showRememberMe && (
                <Form.Item name="remember" valuePropName="checked" style={{ marginBottom: 0 }}>
                  <label 
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer'
                    }}
                  >
                    <input 
                      type="checkbox" 
                      style={{
                        marginRight: '12px',
                        width: '18px',
                        height: '18px',
                        accentColor: primaryColor,
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    />
                    <span 
                      style={{
                        fontSize: '14px',
                        color: '#6b7280',
                        fontWeight: '500',
                        transition: 'color 0.2s ease'
                      }}
                    >
                      Remember me
                    </span>
                  </label>
                </Form.Item>
              )}
              {showForgotPassword && (
                <Link 
                  onClick={handleForgotPasswordClick}
                  style={{
                    fontSize: '14px',
                    color: primaryColor,
                    fontWeight: '600',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    transition: 'color 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = '#4f46e5';
                    e.currentTarget.style.textDecoration = 'underline';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = primaryColor;
                    e.currentTarget.style.textDecoration = 'none';
                  }}
                >
                  Forgot password?
                </Link>
              )}
            </div>

            <Form.Item style={{ marginBottom: '2rem' }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                style={{
                  width: '100%',
                  height: '56px',
                  fontSize: '18px',
                  fontWeight: '700',
                  borderRadius: '16px',
                  background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                  border: 'none',
                  boxShadow: `0 10px 25px -5px ${primaryColor}66`,
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  color: 'white'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = `0 15px 35px -5px ${primaryColor}99`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = `0 10px 25px -5px ${primaryColor}66`;
                }}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </Form.Item>
          </Form>

          {showGoogleLogin && (
            <>
              <Divider 
                style={{
                  margin: '2rem 0',
                  borderColor: '#e5e7eb'
                }}
              >
                <span 
                  style={{
                    color: '#9ca3af',
                    fontSize: '14px',
                    fontWeight: '600',
                    backgroundColor: 'white',
                    padding: '0 16px'
                  }}
                >
                  OR
                </span>
              </Divider>

              {/* Google Login Button - rendered by Google Identity Services */}
              <div
                ref={googleButtonRef}
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              />
              {googleLoading && (
                <div style={{ textAlign: 'center', marginTop: '8px', color: '#6b7280', fontSize: '14px' }}>
                  Signing in with Google...
                </div>
              )}
            </>
          )}

          {/* Register Link */}
          {showRegisterLink && (
            <div 
              style={{
                textAlign: 'center',
                marginTop: '2rem'
              }}
            >
              <Text 
                style={{
                  color: '#6b7280',
                  fontSize: '15px'
                }}
              >
                Don't have an account?{' '}
                <Link 
                  onClick={handleRegisterClick}
                  style={{
                    color: primaryColor,
                    fontWeight: '600',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    transition: 'color 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = '#4f46e5';
                    e.currentTarget.style.textDecoration = 'underline';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = primaryColor;
                    e.currentTarget.style.textDecoration = 'none';
                  }}
                >
                  Sign up for free
                </Link>
              </Text>
            </div>
          )}
        </Card>

        {/* Footer Information */}
        <div 
          style={{
            textAlign: 'center',
            marginTop: '2rem'
          }}
        >
          <Text 
            style={{
              color: 'rgba(255, 255, 255, 0.8)',
              fontSize: '13px',
              lineHeight: '1.6'
            }}
          >
            By signing in, you agree to our{' '}
            <Link 
              style={{
                color: 'rgba(255, 255, 255, 0.9)',
                textDecoration: 'none',
                cursor: 'pointer',
                transition: 'color 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = 'white';
                e.currentTarget.style.textDecoration = 'underline';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)';
                e.currentTarget.style.textDecoration = 'none';
              }}
            >
              Terms of Service
            </Link>
            {' '}and{' '}
            <Link 
              style={{
                color: 'rgba(255, 255, 255, 0.9)',
                textDecoration: 'none',
                cursor: 'pointer',
                transition: 'color 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = 'white';
                e.currentTarget.style.textDecoration = 'underline';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)';
                e.currentTarget.style.textDecoration = 'none';
              }}
            >
              Privacy Policy
            </Link>
          </Text>
        </div>
      </div>
    </div>
  );
};

export default Login;